import React, { useState } from 'react';
import { api, HelmSuggestion } from './api';
import { Icon } from './icons';

// Framed as the USER's own action, not a note aimed at them — a response is a
// reply they can send, an action item is theirs to take on.
const KIND_LABEL: Record<string, string> = {
  response: 'A reply you could send',
  action_item: 'A task for you',
  doc_link: 'Related doc',
  knowledge_gap: 'Knowledge gap',
};
const KIND_ICON: Record<string, string> = {
  response: '💬', action_item: '✅', doc_link: '📎', knowledge_gap: '💡',
};

export function SuggestionCards({
  suggestions,
  channelId,
  onAccept,
  onDismiss,
  onInsertDraft,
}: {
  suggestions: HelmSuggestion[];
  channelId: string;
  onAccept: (s: HelmSuggestion) => void;
  onDismiss: (s: HelmSuggestion) => void;
  onInsertDraft?: (text: string) => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const relevant = suggestions.filter((s) => s.channel_id === channelId && s.status === 'pending');

  if (relevant.length === 0) return null;

  const accept = async (s: HelmSuggestion) => {
    setBusy(s.id);
    try {
      await api.acceptSuggestion(s.id);
      if (s.kind === 'response' && onInsertDraft) {
        onInsertDraft(s.payload.draft || '');
      }
      onAccept(s);
    } catch { /* ignore */ }
    finally { setBusy(null); }
  };

  const dismiss = async (s: HelmSuggestion) => {
    setBusy(s.id);
    try {
      await api.dismissSuggestion(s.id);
      onDismiss(s);
    } catch { /* ignore */ }
    finally { setBusy(null); }
  };

  return (
    <div className="fx-suggestions" role="region" aria-label="Helm suggestions">
      {relevant.slice(0, 3).map((s) => (
        <div key={s.id} className={`fx-suggestion-card fx-suggestion-${s.kind}`}>
          <div className="fx-suggestion-header">
            <span className="fx-suggestion-icon" aria-hidden="true">{KIND_ICON[s.kind] || '🔔'}</span>
            <span className="fx-suggestion-label">Helm drafted for you</span>
            <span className="fx-suggestion-kind">{KIND_LABEL[s.kind] || s.kind}</span>
            {s.confidence >= 0.8 && <span className="fx-suggestion-badge">High confidence</span>}
          </div>
          <div className="fx-suggestion-body">
            {s.kind === 'response' && (
              <>
                <div className="fx-suggestion-draft">{s.payload.draft}</div>
                {s.payload.sources?.length > 0 && (
                  <div className="fx-suggestion-sources">
                    {s.payload.sources.map((src: { title: string; url: string }, i: number) => (
                      src.url ? (
                        <a key={i} href={src.url} target="_blank" rel="noopener noreferrer" className="fx-suggestion-source">
                          🔗 {src.title}
                        </a>
                      ) : null
                    ))}
                  </div>
                )}
              </>
            )}
            {s.kind === 'action_item' && (
              <div className="fx-suggestion-text">
                {s.payload.description}
                {s.payload.due_hint && <span className="fx-suggestion-due"> · {s.payload.due_hint}</span>}
              </div>
            )}
            {s.kind === 'doc_link' && (
              <div className="fx-suggestion-text">
                <strong>{s.payload.node_title}</strong>
                {s.payload.relevance_reason && <span className="fx-suggestion-reason"> — {s.payload.relevance_reason}</span>}
              </div>
            )}
            {s.kind === 'knowledge_gap' && (
              <div className="fx-suggestion-text">
                {s.payload.question}
                {s.payload.suggested_doc_title && (
                  <span className="fx-suggestion-reason"> → Create: "{s.payload.suggested_doc_title}"</span>
                )}
              </div>
            )}
          </div>
          <div className="fx-suggestion-actions">
            {s.kind === 'response' ? (
              <>
                <button type="button" className="fx-suggestion-btn fx-suggestion-accept"
                  disabled={busy === s.id} onClick={() => accept(s)}>
                  <Icon name="send" size={12} /> Send
                </button>
                <button type="button" className="fx-suggestion-btn fx-suggestion-edit"
                  disabled={busy === s.id}
                  onClick={() => { onInsertDraft?.(s.payload.draft || ''); dismiss(s); }}>
                  Edit
                </button>
              </>
            ) : (
              <button type="button" className="fx-suggestion-btn fx-suggestion-accept"
                disabled={busy === s.id} onClick={() => accept(s)}>
                Accept
              </button>
            )}
            <button type="button" className="fx-suggestion-btn fx-suggestion-dismiss"
              disabled={busy === s.id} onClick={() => dismiss(s)}>
              Dismiss
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
